/**
 * 送れていない録音を端末（IndexedDB）に置いておく場所（PRODUCT_DEV-1270）
 *
 * 送る前に入れ、送り終えたら消す。電波が弱い・圏外で送れなかった録音は残り、
 * つながったときか次に開いたときに送り直す（`pendingRecordings.ts`）。
 *
 * ⚠ ブラウザによっては使えない（プライベートブラウズ・保存を止めた設定）。
 * どの関数も投げずに「できなかった」を返すので、呼ぶ側は今までどおりの送信を続ければよい。
 * iPhone の Safari は、ホーム画面に置いていない普通のタブだと中身を消すことがある。
 */
import type { PendingRecording } from "./types";

const DB_NAME = "unizo-offline-recording";
const STORE = "pending";
const VERSION = 1;

let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
  if (dbPromise) return dbPromise;
  const opening = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE, { keyPath: "id" });
        }
      };
      req.onsuccess = () => {
        const db = req.result;
        // 閉じられたら（iOS で裏へ回したあと・別のタブが版を上げたとき）次に使うとき開き直す
        db.onclose = () => {
          dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  }).then((db) => {
    // 開けなかったら次に呼ばれたときにもう一度試す
    if (!db) dbPromise = null;
    return db;
  });
  dbPromise = opening;
  return opening;
};

const run = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<{ ok: true; value: T } | { ok: false }> => {
  const db = await openDb();
  if (!db) return { ok: false };
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve({ ok: true, value: req.result });
      tx.onerror = () => resolve({ ok: false });
      tx.onabort = () => resolve({ ok: false });
    } catch {
      // 閉じた接続を使い続けないよう、次は開き直す
      dbPromise = null;
      resolve({ ok: false });
    }
  });
};

/** 入れる（同じ id は上書き）。入れられたら true */
export const putPending = async (item: PendingRecording): Promise<boolean> =>
  (await run("readwrite", (s) => s.put(item))).ok;

export const listPending = async (): Promise<PendingRecording[]> => {
  const r = await run<PendingRecording[]>("readonly", (s) => s.getAll());
  return r.ok ? r.value : [];
};

/**
 * 1件読む。`ok: false` は「読めなかった」で、「無い」（ok: true, item: null）とは分ける
 * （読めなかったのに「送りました」と出さないため）
 */
export const readPending = async (
  id: string,
): Promise<{ ok: true; item: PendingRecording | null } | { ok: false }> => {
  const r = await run<PendingRecording | undefined>("readonly", (s) => s.get(id));
  return r.ok ? { ok: true, item: r.value ?? null } : { ok: false };
};

/** 1件読む（読めなかったときも null） */
export const getPending = async (id: string): Promise<PendingRecording | null> => {
  const r = await readPending(id);
  return r.ok ? r.item : null;
};

/**
 * まだ端末にあるときだけ書く（送っている途中で人が「消す」を押した録音を書き戻さない）。
 * 読んでから書くまでの間に消されることはありうるが、そのときは次の送信で消える
 */
export const putPendingIfExists = async (item: PendingRecording): Promise<boolean> => {
  const r = await readPending(item.id);
  return r.ok && r.item ? putPending(item) : false;
};

export const deletePending = async (id: string): Promise<boolean> =>
  (await run("readwrite", (s) => s.delete(id))).ok;

/**
 * ブラウザに「この保存領域は消さないで」と頼む（できなくても続ける）。
 * Chrome は条件次第で認め、Safari はホーム画面に置いたときだけ効く
 */
export const requestPersistentStorage = async (): Promise<void> => {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // 頼めなくても保存はできる
  }
};
